/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";

// Crear el contexto
const SettingsContext = createContext();

// Proveedor del contexto
export const SettingsProvider = ({ children }) => {
  // Estados de configuración
  const [enableHighAccuracy, setEnableHighAccuracy] = useState(false);
  const [gpsEnabled, setGpsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [batteryOptimization, setBatteryOptimization] = useState(false);
  const [isPreoperationalActive, setIsPreoperationalActive] = useState(false);

  // Cargar configuración desde localStorage al inicializar
  useEffect(() => {
    const loadSetting = (key, setter, defaultValue = false) => {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        setter(JSON.parse(stored));
      } else {
        setter(defaultValue);
      }
    };

    loadSetting("enableHighAccuracy", setEnableHighAccuracy, false);
    loadSetting("gpsEnabled", setGpsEnabled, true);
    loadSetting("soundEnabled", setSoundEnabled, true);
    loadSetting("notificationsEnabled", setNotificationsEnabled, true);
    loadSetting("autoRefresh", setAutoRefresh, true);
    loadSetting("batteryOptimization", setBatteryOptimization, false);
  }, []);

  // Guardar configuración en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem("enableHighAccuracy", JSON.stringify(enableHighAccuracy));
  }, [enableHighAccuracy]);

  useEffect(() => {
    localStorage.setItem("gpsEnabled", JSON.stringify(gpsEnabled));
  }, [gpsEnabled]);

  useEffect(() => {
    localStorage.setItem("soundEnabled", JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem("notificationsEnabled", JSON.stringify(notificationsEnabled));
  }, [notificationsEnabled]);

  useEffect(() => {
    localStorage.setItem("autoRefresh", JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  useEffect(() => {
    localStorage.setItem("batteryOptimization", JSON.stringify(batteryOptimization));
  }, [batteryOptimization]);

  // Funciones toggle
  const toggleHighAccuracy = () => setEnableHighAccuracy(prev => !prev);
  const toggleGPS = () => setGpsEnabled(prev => !prev);
  const toggleSound = () => setSoundEnabled(prev => !prev);
  const toggleNotifications = () => setNotificationsEnabled(prev => !prev);
  const toggleAutoRefresh = () => setAutoRefresh(prev => !prev);
  const toggleBatteryOptimization = () => setBatteryOptimization(prev => !prev);

  // Solicitar permisos de notificación cuando se activen
  useEffect(() => {
    if (notificationsEnabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [notificationsEnabled]);

  // Memoizar el valor del contexto para evitar recreaciones innecesarias
  const contextValue = useMemo(
    () => ({
      // Estados
      enableHighAccuracy,
      gpsEnabled,
      soundEnabled,
      notificationsEnabled,
      autoRefresh,
      batteryOptimization,
      isPreoperationalActive,
      // Setters
      setEnableHighAccuracy,
      setGpsEnabled,
      setSoundEnabled,
      setNotificationsEnabled,
      setAutoRefresh,
      setBatteryOptimization,
      setIsPreoperationalActive,
      // Toggles
      toggleHighAccuracy,
      toggleGPS,
      toggleSound,
      toggleNotifications,
      toggleAutoRefresh,
      toggleBatteryOptimization,
    }),
    [
      enableHighAccuracy,
      gpsEnabled,
      soundEnabled,
      notificationsEnabled,
      autoRefresh,
      batteryOptimization,
      isPreoperationalActive,
    ]
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Validar las props del proveedor
SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Hook para usar el contexto
export const useSettings = () => useContext(SettingsContext);
